import { useCallback, useMemo, useState } from 'react';
import {
    defaultConfig,
    loadConfig,
    moveColumn,
    sanitizeConfig,
    saveConfig,
    toggleColumn,
} from '../components/common/columns/columnModel';

/**
 * Column visibility / order for one table, persisted per browser (localStorage
 * `um_ui_cols:<tableKey>`). Without a tableKey nothing is persisted and the hook returns
 * `enabled: false` (no Columns menu).
 *
 * The saved config is reconciled with the current declarations on every render, so a column
 * added or removed in code never breaks an older saved choice.
 *
 * Returns { enabled, config, toggle(key), move(key, delta), reset() }.
 */
export default function useColumnPreference(tableKey, columns) {
    const [saved, setSaved] = useState(() => (tableKey ? loadConfig(tableKey, columns) : null));
    // Declarations that matter for reconciling; JSX/handlers in `render` do not.
    const signature = columns.map((c) => `${c.key}:${c.locked ? 1 : 0}:${c.defaultVisible === false ? 0 : 1}`).join('|');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const config = useMemo(() => sanitizeConfig(saved, columns), [saved, signature]);

    const commit = useCallback(
        (next) => {
            const clean = sanitizeConfig(next, columns);
            setSaved(clean);
            saveConfig(tableKey, clean, columns);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tableKey, signature]
    );

    return {
        enabled: Boolean(tableKey),
        config,
        toggle: (key) => commit(toggleColumn(config, columns, key)),
        move: (key, delta) => commit(moveColumn(config, columns, key, delta)),
        reset: () => commit(defaultConfig(columns)),
    };
}
